// Internal loading of supplier brief responses, with the §7 privacy rule baked
// in: Fusion sees ENGAGEMENT, never unsubmitted CONTENT.
//
// "Started, not submitted" means a draft exists — it must never expose the
// draft's figures, text or attachments. This is not a UI-hiding matter: the
// data must not be fetched to the internal client at all. So there are two
// distinct loads:
//   - submitted responses  → full content (only is_submitted = true rows)
//   - draft presence        → existence + last-activity timestamp ONLY
//
// Callers for internal surfaces MUST use these rather than a bare
// `select('*')`, which would pull draft prices and line items into the client.

import { supabase as defaultClient } from '../supabaseClient';

// The only columns an internal caller may read for an UNSUBMITTED response.
// Existence + which supplier + activity timestamps. No total_price, no
// vat_rate, no notes/terms, and never the line-item or attachment sub-tables.
export const SAFE_DRAFT_COLUMNS = Object.freeze([
  'id', 'brief_id', 'supplier_id', 'recipient_id', 'is_submitted', 'created_at', 'updated_at',
]);

// Content columns that must NEVER appear in a draft read — the guard the
// regression test asserts against, and a living list if new content fields are
// added to brief_responses.
export const CONTENT_COLUMNS = Object.freeze([
  'total_price', 'vat_rate', 'notes', 'terms', 'lead_time', 'payment_terms',
]);

// Submitted responses WITH content. is_submitted = true is applied here so
// draft content is never content-loaded for internal users.
export async function fetchSubmittedResponses(briefId, client = defaultClient) {
  return client
    .from('brief_responses')
    .select('*')
    .eq('brief_id', briefId)
    .eq('is_submitted', true);
}

// Draft PRESENCE for engagement/at-risk — existence + last activity only, via
// the safe column allowlist. Returns unsubmitted rows so the caller can mark a
// recipient "started, not submitted" and show "Last active …" without ever
// seeing what was written.
export async function fetchDraftPresence(briefId, client = defaultClient) {
  return client
    .from('brief_responses')
    .select(SAFE_DRAFT_COLUMNS.join(', '))
    .eq('brief_id', briefId)
    .eq('is_submitted', false);
}

// Draft PRESENCE across many briefs (the Briefs list) — existence + linkage
// only, same no-content allowlist. Returns a Map<brief_id, Set<supplier_id>>
// so a list row can mark "started" recipients without any draft content.
export async function fetchDraftPresenceForBriefs(briefIds, client = defaultClient) {
  const ids = Array.from(new Set((briefIds || []).filter(Boolean)));
  const out = new Map();
  if (ids.length === 0) return out;
  const { data } = await client
    .from('brief_responses')
    .select('brief_id, supplier_id, is_submitted')
    .eq('is_submitted', false)
    .in('brief_id', ids);
  for (const r of data || []) {
    if (!r.supplier_id) continue;
    if (!out.has(r.brief_id)) out.set(r.brief_id, new Set());
    out.get(r.brief_id).add(r.supplier_id);
  }
  return out;
}

// Set of supplier_ids that have a draft in progress, from a draft-presence
// result — the `hasDraft` signal engagementState needs.
export function draftSupplierIds(draftRows) {
  return new Set((draftRows || []).map(r => r.supplier_id).filter(Boolean));
}
